// 新建文件: src/modules/navigation/services/expand-state-manager.ts
import { Service } from '@toeverything/infra';
import type { GlobalCache } from '../../storage';

interface ExpandStateEntry {
  expanded: boolean;
  lastAccessed: number;
}

export class ExpandStateManager extends Service {
  private readonly STORAGE_KEY = 'navigation.expand.states';
  private readonly MAX_STATES = 500;
  private readonly CLEANUP_THRESHOLD = 0.8; // 80% 时开始清理
  private readonly MIGRATION_KEY = 'navigation.expand.migrated'; // 迁移标记

  constructor(private readonly globalCache: GlobalCache) {
    super();
    // 检查是否已经迁移过
    const hasMigrated = this.globalCache.get<boolean>(this.MIGRATION_KEY);
    if (!hasMigrated) {
      this.migrateFromLegacyCache();
      // 标记已迁移，避免重复执行
      this.globalCache.set(this.MIGRATION_KEY, true);
    }
  }

  getExpandState(nodeId: string, defaultState = true): boolean {
    const states = this.getStates();
    const entry = states[nodeId];

    if (entry) {
      // 更新访问时间
      entry.lastAccessed = Date.now();
      this.saveStates(states);
      return entry.expanded;
    }

    return defaultState;
  }

  setExpandState(nodeId: string, expanded: boolean): void {
    const states = this.getStates();

    // 检查是否需要清理
    if (
      Object.keys(states).length >=
      this.MAX_STATES * this.CLEANUP_THRESHOLD
    ) {
      this.cleanup(states);
    }

    states[nodeId] = {
      expanded,
      lastAccessed: Date.now(),
    };

    this.saveStates(states);
  }

  private getStates(): Record<string, ExpandStateEntry> {
    try {
      return (
        this.globalCache.get<Record<string, ExpandStateEntry>>(
          this.STORAGE_KEY
        ) ?? {}
      );
    } catch (error) {
      console.warn('Failed to load expand states:', error);
      return {};
    }
  }

  private saveStates(states: Record<string, ExpandStateEntry>): void {
    try {
      this.globalCache.set(this.STORAGE_KEY, states);
    } catch (error) {
      console.warn('Failed to save expand states, attempting cleanup:', error);
      // 存储失败时强制清理
      this.cleanup(states, 0.5); // 保留 50%
      try {
        this.globalCache.set(this.STORAGE_KEY, states);
      } catch (retryError) {
        console.error(
          'Failed to save expand states after cleanup:',
          retryError
        );
      }
    }
  }

  private cleanup(
    states: Record<string, ExpandStateEntry>,
    keepRatio = 0.6
  ): void {
    const entries = Object.entries(states);
    const keepCount = Math.floor(entries.length * keepRatio);

    // 按最后访问时间排序，保留最近使用的
    const sortedEntries = entries.sort(
      (a, b) => b[1].lastAccessed - a[1].lastAccessed
    );
    const keptEntries = sortedEntries.slice(0, keepCount);

    // 清空原对象并重新填充
    Object.keys(states).forEach(key => delete states[key]);
    keptEntries.forEach(([key, value]) => {
      states[key] = value;
    });

    console.log(
      `Cleaned up expand states: ${entries.length} -> ${keptEntries.length}`
    );
  }

  // 清理无效节点的状态
  cleanupInvalidNodes(validNodeIds: Set<string>): void {
    const states = this.getStates();
    const originalCount = Object.keys(states).length;

    Object.keys(states).forEach(nodeId => {
      if (!validNodeIds.has(nodeId)) {
        delete states[nodeId];
      }
    });

    const newCount = Object.keys(states).length;
    if (newCount !== originalCount) {
      this.saveStates(states);
      console.log(`Cleaned up invalid nodes: ${originalCount} -> ${newCount}`);
    }
  }

  // 获取统计信息
  getStats(): {
    totalStates: number;
    oldestAccess: number;
    newestAccess: number;
  } {
    const states = this.getStates();
    const entries = Object.values(states);

    if (entries.length === 0) {
      return { totalStates: 0, oldestAccess: 0, newestAccess: 0 };
    }

    const accessTimes = entries.map(e => e.lastAccessed);
    return {
      totalStates: entries.length,
      oldestAccess: Math.min(...accessTimes),
      newestAccess: Math.max(...accessTimes),
    };
  }
  // 在 ExpandStateManager 类中添加这个方法

  /**
   * 迁移旧的分散式展开状态到新的集中式存储
   * 同时清理旧的缓存键
   */
  migrateFromLegacyCache(): void {
    try {
      console.log('Starting migration from legacy expand state cache...');

      // 1. 获取所有缓存键
      const allKeys = this.globalCache.keys();
      const legacyKeys = allKeys.filter(key =>
        key.startsWith('folder.expand.')
      );

      if (legacyKeys.length === 0) {
        console.log('No legacy expand state cache found, migration skipped.');
        return;
      }

      console.log(`Found ${legacyKeys.length} legacy expand state entries`);

      // 2. 读取现有的集中式状态
      const currentStates = this.getStates();
      let migratedCount = 0;

      // 3. 迁移每个旧的展开状态
      legacyKeys.forEach(key => {
        try {
          // 提取 nodeId (移除 'folder.expand.' 前缀)
          const nodeId = key.replace('folder.expand.', '');

          // 读取旧的展开状态
          const legacyExpanded = this.globalCache.get<boolean>(key);

          if (typeof legacyExpanded === 'boolean') {
            // 只有当新状态中不存在这个节点时才迁移
            if (!currentStates[nodeId]) {
              currentStates[nodeId] = {
                expanded: legacyExpanded,
                lastAccessed: Date.now() - Math.random() * 86400000, // 随机分配过去24小时内的时间
              };
              migratedCount++;
            }
          }

          // 4. 删除旧的缓存键
          this.globalCache.del(key);
        } catch (error) {
          console.warn(`Failed to migrate legacy key ${key}:`, error);
        }
      });

      // 5. 保存迁移后的状态
      if (migratedCount > 0) {
        // 如果迁移的数据太多，先清理一下
        if (Object.keys(currentStates).length > this.MAX_STATES) {
          console.log('Too many states after migration, cleaning up...');
          this.cleanup(currentStates, 0.7); // 保留 70%
        }

        this.saveStates(currentStates);
        console.log(
          `Successfully migrated ${migratedCount} expand states and cleaned up ${legacyKeys.length} legacy cache entries`
        );
      } else {
        console.log('No valid legacy states found to migrate');
      }

      // 6. 记录迁移统计
      const stats = this.getStats();
      console.log('Post-migration stats:', stats);
    } catch (error) {
      console.error('Failed to migrate legacy expand state cache:', error);
    }
  }
}
